import { useState, useMemo, JSX } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Collapse,
  Paper,
  ActionIcon,
} from '@mantine/core';
import { IconPlus, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Patient, Encounter, MedicationStatement } from '@medplum/fhirtypes';
import { MedicationListItem, MedicationAction } from './MedicationListItem';
import { MedicationModal } from './MedicationModal';
import { StopMedicationModal } from './StopMedicationModal';
import { useMedicationActions } from '@/hooks/useMedicationActions';
import { isStopped, getContextEncounterId, createNewMedicationStatement } from '@/utils/medication.utils';

export interface MedicationListProps {
  medications: MedicationStatement[];
  onChange: (medications: MedicationStatement[]) => void;
  currentEncounterId?: string;
  patient: Patient;
  encounter: Encounter;
}

export const MedicationList = ({
  medications,
  onChange,
  currentEncounterId,
  patient,
  encounter,
}: MedicationListProps): JSX.Element => {
  const { t } = useTranslation(['patient-pages']);

  // Use the medication actions hook for server operations
  const { saveMedication, stopMedication, deleteMedication } = useMedicationActions(
    patient,
    encounter
  );

  // Modal states
  const [medicationModalOpened, setMedicationModalOpened] = useState(false);
  const [stopModalOpened, setStopModalOpened] = useState(false);
  const [editingMedication, setEditingMedication] = useState<MedicationStatement | undefined>();
  const [editingIndex, setEditingIndex] = useState<number | undefined>();
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingIndex, setDeletingIndex] = useState<number | undefined>();

  // Section collapse states
  const [stoppedThisEncounterCollapsed, setStoppedThisEncounterCollapsed] = useState(false);
  const [previouslyStoppedCollapsed, setPreviouslyStoppedCollapsed] = useState(true);

  // Group medications by status and encounter
  const { activeMedications, stoppedThisEncounter, previouslyStopped } = useMemo(() => {
    const active: { medication: MedicationStatement; index: number }[] = [];
    const stoppedThis: { medication: MedicationStatement; index: number }[] = [];
    const stoppedPrev: { medication: MedicationStatement; index: number }[] = [];

    medications.forEach((medication, index) => {
      // Skip entered-in-error medications entirely - they're corrections, not clinical data
      if (medication.status === 'entered-in-error') {
        return;
      }

      const medicationIsStopped = isStopped(medication);
      const encounterId = getContextEncounterId(medication);

      if (!medicationIsStopped) {
        active.push({ medication, index });
      } else if (currentEncounterId && encounterId === currentEncounterId) {
        stoppedThis.push({ medication, index });
      } else {
        stoppedPrev.push({ medication, index });
      }
    });

    return {
      activeMedications: active,
      stoppedThisEncounter: stoppedThis,
      previouslyStopped: stoppedPrev,
    };
  }, [medications, currentEncounterId]);

  // Handlers
  const handleAddMedication = () => {
    // Create a new empty medication with patient/encounter context
    const newMed = createNewMedicationStatement(patient, encounter);
    setEditingMedication(newMed);
    setEditingIndex(undefined);
    setModalMode('add');
    setMedicationModalOpened(true);
  };

  const handleAction = async (index: number, action: MedicationAction) => {
    const medication = medications[index];

    switch (action) {
      case 'edit':
        setEditingMedication(medication);
        setEditingIndex(index);
        setModalMode('edit');
        setMedicationModalOpened(true);
        break;

      case 'stop':
        setEditingMedication(medication);
        setEditingIndex(index);
        setStopModalOpened(true);
        break;

      case 'remove': {
        // If the medication has an ID, it's saved on server - soft delete it
        if (medication.id) {
          setDeletingIndex(index);
          try {
            await deleteMedication(medication.id);
            // Remove from local state after successful server deletion
            const updated = medications.filter((_, i) => i !== index);
            onChange(updated);
          } catch (error) {
            notifications.show({
              color: 'red',
              title: t('medications.deleteErrorTitle', {
                defaultValue: 'Failed to remove medication',
              }),
              message: String(error),
            });
          } finally {
            setDeletingIndex(undefined);
          }
        } else {
          // Not saved yet - just remove from local state
          const updated = medications.filter((_, i) => i !== index);
          onChange(updated);
        }
        break;
      }
    }
  };

  const handleSaveMedication = (medication: MedicationStatement) => {
    if (editingIndex !== undefined) {
      // Update existing
      const updated = medications.map((m, i) =>
        i === editingIndex ? medication : m
      );
      onChange(updated);
    } else {
      // Add new
      onChange([...medications, medication]);
    }
  };

  const handleStopMedication = (stoppedMedication: MedicationStatement) => {
    if (editingIndex === undefined) {
      return;
    }

    const updated = medications.map((m, i) =>
      i === editingIndex ? stoppedMedication : m
    );
    onChange(updated);
  };

  // Section header component
  const SectionHeader = ({
    title,
    count,
    collapsed,
    onToggle,
    color = 'gray',
  }: {
    title: string;
    count: number;
    collapsed: boolean;
    onToggle: () => void;
    color?: string;
  }) => (
    <Group
      gap="xs"
      style={{ cursor: 'pointer' }}
      onClick={onToggle}
      mb="xs"
    >
      <ActionIcon variant="subtle" size="xs" color={color}>
        {collapsed ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
      </ActionIcon>
      <Text fw={600} size="sm" c={color}>
        {title}
      </Text>
      <Badge size="xs" variant="light" color={color}>
        {count}
      </Badge>
    </Group>
  );

  return (
    <Stack gap="md">
      {/* Header */}
      <Text fw={600}>{t('medications.title')}</Text>

      {/* Empty state */}
      {medications.length === 0 ? (
        <>
          <Paper withBorder radius="md" p="xl" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">
              {t('medications.none')}
            </Text>
          </Paper>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="sm"
            onClick={handleAddMedication}
          >
            {t('medications.add')}
          </Button>
        </>
      ) : (
        <>
          {/* Active Medications */}
          {activeMedications.length > 0 && (
            <Stack gap="xs">
              <Group gap="xs">
                <Text fw={600} size="sm">
                  {t('medications.sections.active')}
                </Text>
                <Badge size="xs" variant="light" mt={3}>
                  {activeMedications.length}
                </Badge>
              </Group>
              {activeMedications.map(({ medication, index }) => (
                <MedicationListItem
                  key={medication.id || `new-${index}`}
                  medication={medication}
                  onAction={(action) => handleAction(index, action)}
                  isDeleting={deletingIndex === index}
                />
              ))}
            </Stack>
          )}

          {/* Add Medication Button */}
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="sm"
            onClick={handleAddMedication}
          >
            {t('medications.add')}
          </Button>

          {/* Stopped This Encounter */}
          {stoppedThisEncounter.length > 0 && (
            <Stack gap="xs">
              <SectionHeader
                title={t('medications.sections.stoppedThisEncounter')}
                count={stoppedThisEncounter.length}
                collapsed={stoppedThisEncounterCollapsed}
                onToggle={() =>
                  setStoppedThisEncounterCollapsed(!stoppedThisEncounterCollapsed)
                }
                color="red"
              />
              <Collapse in={!stoppedThisEncounterCollapsed}>
                <Stack gap="xs">
                  {stoppedThisEncounter.map(({ medication, index }) => (
                    <MedicationListItem
                      key={medication.id || `stopped-${index}`}
                      medication={medication}
                      onAction={(action) => handleAction(index, action)}
                      isDeleting={deletingIndex === index}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          )}

          {/* Previously Stopped */}
          {previouslyStopped.length > 0 && (
            <Stack gap="xs">
              <SectionHeader
                title={t('medications.sections.previouslyStopped')}
                count={previouslyStopped.length}
                collapsed={previouslyStoppedCollapsed}
                onToggle={() =>
                  setPreviouslyStoppedCollapsed(!previouslyStoppedCollapsed)
                }
                color="gray"
              />
              <Collapse in={!previouslyStoppedCollapsed}>
                <Stack gap="xs">
                  {previouslyStopped.map(({ medication, index }) => (
                    <MedicationListItem
                      key={medication.id || `prev-stopped-${index}`}
                      medication={medication}
                      onAction={(action) => handleAction(index, action)}
                      isDeleting={deletingIndex === index}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          )}
        </>
      )}

      {/* Modals */}
      <MedicationModal
        opened={medicationModalOpened}
        onClose={() => setMedicationModalOpened(false)}
        onSave={handleSaveMedication}
        onSaveToServer={saveMedication}
        medication={editingMedication}
        mode={modalMode}
      />

      <StopMedicationModal
        opened={stopModalOpened}
        onClose={() => setStopModalOpened(false)}
        onConfirm={handleStopMedication}
        onStopToServer={stopMedication}
        medication={editingMedication}
      />
    </Stack>
  );
};
